import { ShaderProgram } from '../renderer/ShaderProgram';
import type { Renderable } from '../renderer/RenderPipeline';
import type { Camera } from '../renderer/Camera';

import vertSrc from '../shaders/debug_circle.vert.glsl?raw';
import fragSrc from '../shaders/debug_circle.frag.glsl?raw';

const SEGMENTS = 48;
const MAX_CIRCLES = 64;

/**
 * Renders flat GL_LINE_LOOP circles on the XZ plane.
 * Supports N circles for per-ship footprint visualisation.
 * Pre-allocated — zero allocation in setCircles()/render().
 */
export class DebugCircleRenderer implements Renderable {
  private _shader: ShaderProgram;
  private _vao: WebGLVertexArrayObject;
  private _active = false;
  private _count = 0;
  private _cx = new Float32Array(MAX_CIRCLES);
  private _cy = new Float32Array(MAX_CIRCLES);
  private _cz = new Float32Array(MAX_CIRCLES);
  private _cr = new Float32Array(MAX_CIRCLES);
  private _r = 0.2;
  private _g = 0.9;
  private _b = 0.4;

  constructor(gl: WebGL2RenderingContext) {
    this._shader = new ShaderProgram(gl, vertSrc, fragSrc);
    this._vao = gl.createVertexArray()!;
    gl.bindVertexArray(this._vao);

    const verts = new Float32Array(SEGMENTS * 2);
    for (let i = 0; i < SEGMENTS; i++) {
      const a = (i / SEGMENTS) * Math.PI * 2;
      verts[i * 2] = Math.cos(a);
      verts[i * 2 + 1] = Math.sin(a);
    }
    const buf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
  }

  setActive(v: boolean): void { this._active = v; }
  setColor(r: number, g: number, b: number): void { this._r = r; this._g = g; this._b = b; }

  /** Set a single circle (used by FootprintDebugTab). */
  setSingle(x: number, y: number, z: number, radius: number): void {
    this._count = 1;
    this._cx[0] = x; this._cy[0] = y; this._cz[0] = z;
    this._cr[0] = radius;
  }

  /** Copy positions + radii from formation arrays. Zero-allocation. */
  setFromFormation(
    n: number,
    px: Float32Array, py: Float32Array, pz: Float32Array,
    fp: Float32Array,
  ): void {
    this._count = n;
    for (let i = 0; i < n; i++) {
      this._cx[i] = px[i];
      this._cy[i] = py[i];
      this._cz[i] = pz[i];
      this._cr[i] = fp[i];
    }
  }

  render(gl: WebGL2RenderingContext, camera: Camera): void {
    if (!this._active || this._count === 0) return;
    this._shader.use();
    this._shader.setMat4('u_projection', camera.projection.data);
    this._shader.setMat4('u_view', camera.view.data);
    this._shader.setVec3('u_color', this._r, this._g, this._b);
    gl.bindVertexArray(this._vao);
    for (let i = 0; i < this._count; i++) {
      this._shader.setVec3('u_center', this._cx[i], this._cy[i], this._cz[i]);
      this._shader.setFloat('u_radius', this._cr[i]);
      gl.drawArrays(gl.LINE_LOOP, 0, SEGMENTS);
    }
    gl.bindVertexArray(null);
  }

  dispose(): void {
    this._shader.dispose();
  }
}
